import { FC, MouseEvent } from "react";
import styled from "styled-components";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import {
  Actions,
  CloseButton,
  StyledDialog,
} from "@/components/AlertDialogs/Dialog";
import { Modal } from "@/components/Modal";
import Button from "@/components/Button";
import Text from "@/components/Text";
import { api } from "@/utils/api";
import FormField from "@/components/Forms/FormField";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";

const Heading = styled(Text)`
  margin-bottom: 24px;
`;

interface FormDataT {
  name: string;
}

interface Props {
  onClose: () => void;
  scriptId: number;
  setActiveSceneId: (
    sceneId: number | null,
    setActiveSceneId?: boolean
  ) => void;
}

const SceneCreationModal: FC<Props> = ({
  onClose,
  scriptId,
  setActiveSceneId,
}) => {
  const onCloseWithEventPrevention = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: "",
    },
  });

  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async ({ name }) => {
    try {
      const { id } = await api.post<{ name: string }, { id: number }>(
        "/v1/production/scenes",
        {
          name,
          content: "Scene content goes there",
          production_script_id: scriptId,
        }
      );

      await queryClient.invalidateQueries(["production_projects"]);

      setActiveSceneId?.(id, true);

      onClose();

      alerts.success(`Scene has been successfully created`, name);
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal zIndex={50}>
      <StyledDialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onCloseWithEventPrevention}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Heading $type="heading2">Add scene</Heading>
        <form autoComplete="off" onSubmit={onSubmit}>
          <FormField<FormDataT> name="name" control={control} />
          <Actions>
            <Button
              loading={isSubmitting}
              disabled={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button
              onClick={onCloseWithEventPrevention}
              color="secondary"
              type="button"
            >
              Cancel
            </Button>
          </Actions>
        </form>
      </StyledDialog>
    </Modal>
  );
};

export default SceneCreationModal;
